/**
 * editor2/hooks/useReservationEditor.ts
 * メインオーケストレーターフック
 * 保存・dirty検知・自動変更メモ統合ロジック + 同時編集の保護（三方向マージ）
 *
 * 2026-09-07 同時編集の保護（feature-sidepanel.md「同時編集の保護」参照）:
 *   - base（パネルを開いた時点）を保持し、保存時にサーバー最新（fresh）と三方向マージする。
 *     「パネルを開いた時点の全フィールド」を送っていた従来方式は、他の端末の変更を
 *     先祖返りさせていた。
 *   - パネルを開いている間、reservationSyncService（Pusher / ポーリング）でその予約が
 *     更新されたら、自分が触っていないフィールドは最新値に差し替え（remote）、
 *     自分も触っているフィールドは競合（conflict）として保存時に確認する。
 *   - 保存は onSave(マージ済みペイロード) で親に渡す。親は第1引数をそのまま保存すること。
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { generateAutoChangeMemo, formatAutoMemoEntry } from './useAutoChangeMemo';
import type { EditorReservation, StatusOption } from '../types';
import {
  threeWayMerge,
  diffFields,
  getField,
  isNewerThanBase,
  memoAddedByTheirs,
  type FieldConflict,
  type Resolution,
} from '../conflict/threeWayMerge';
import type { FieldMark } from '../conflict/fieldState';
import { fetchDispatchReservations } from '../../../api/dispatchService';
import { reservationSyncService } from '../../../services/reservationSyncService';
import { toEditorReservation } from '../../../lib/editorDataTransform';
import { logEvent } from '../../../lib/eventLog';

interface UseReservationEditorOptions {
  open: boolean;
  editing: EditorReservation | null;
  onChange?: (updates: Partial<EditorReservation>) => void;
  onSave?: (data: EditorReservation, options?: { keepOpen?: boolean }) => Promise<void>;
  statusOptions?: StatusOption[];
  /** トースト等の通知（fresh 取得失敗など） */
  onNotice?: (message: string, severity: 'info' | 'success' | 'error') => void;
}

export interface RemoteChange {
  field: string;
  /** 相手側の値（editing と同じ表現） */
  value: any;
  /** 相手側の updated_at */
  at: string | null;
  /** 自分も変更している（= 保存時に確認） */
  conflict: boolean;
}

export interface ConflictDialogState {
  open: boolean;
  conflicts: FieldConflict[];
  theirsUpdatedAt: string | null;
  theirMemoAdded: string | null;
}

const EMPTY_CONFLICT: ConflictDialogState = { open: false, conflicts: [], theirsUpdatedAt: null, theirMemoAdded: null };

function isTmpId(id: any): boolean {
  return id == null || id === '' || String(id).startsWith('tmp_');
}

/** editing が toEditorReservation 済みの editor 形か（カレンダーは生レコードを渡してくる） */
function isEditorShaped(obj: Record<string, any>): boolean {
  return Object.prototype.hasOwnProperty.call(obj, '__isNew');
}

export default function useReservationEditor({
  open,
  editing,
  onChange,
  onSave,
  statusOptions,
  onNotice,
}: UseReservationEditorOptions) {
  const [isSaving, setIsSaving] = useState(false);
  const [originalJson, setOriginalJson] = useState<string | null>(null);

  // 編集中のIDを追跡（リセット用）
  const lastEditingIdRef = useRef<string | null>(null);
  const justSavedRef = useRef(false);

  // base（パネルを開いた時点のスナップショット）。originalJson と常に同じ内容を object で持つ
  const baseRef = useRef<Record<string, any> | null>(null);
  // base 時点の updated_at（editing 本体には持たせない: JSON 比較の dirty 判定を汚さないため）
  const baseUpdatedAtRef = useRef<string | null>(null);
  const savingRef = useRef(false);

  // 他端末の更新（パネルを開いてから）
  const [remoteChanges, setRemoteChanges] = useState<Record<string, RemoteChange>>({});
  // 競合ダイアログ
  const [conflictDialog, setConflictDialog] = useState<ConflictDialogState>(EMPTY_CONFLICT);
  const conflictResolverRef = useRef<((r: Record<string, Resolution> | null) => void) | null>(null);

  // 最新のonSave / onChange / editing を保持するRef
  const onSaveRef = useRef(onSave);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const onNoticeRef = useRef(onNotice);
  useEffect(() => { onNoticeRef.current = onNotice; }, [onNotice]);

  const editingRef = useRef(editing);
  useEffect(() => { editingRef.current = editing; }, [editing]);

  const isNewReservation = Boolean(editing?.__isNew);
  const isCopyReservation = Boolean(editing?.__isCopy);

  const setBase = useCallback((obj: Record<string, any> | null) => {
    baseRef.current = obj;
    setOriginalJson(obj ? JSON.stringify(obj) : null);
  }, []);

  // openがtrueになったタイミングで初期状態を保持
  useEffect(() => {
    if (open && editing) {
      const currentId = editing.id ?? editing.page_id;

      // IDが変わった場合（新規オープンや予約切り替え）
      // または、保存直後（justSavedRefがtrue）の場合はオリジナルを更新してdirty解除
      if (lastEditingIdRef.current !== currentId || justSavedRef.current) {
        const idChanged = lastEditingIdRef.current !== currentId;
        setBase(editing);
        // updated_at は editing 自身（toEditorReservation は 2026-09-07 から通す）か、
        // 無ければ同期キャッシュから拾う（カレンダーの生レコードは持っている）
        const cached = !isTmpId(editing.id) ? reservationSyncService.cachedReservations?.get(String(editing.id)) : null;
        baseUpdatedAtRef.current = (editing as any).updated_at ?? cached?.updated_at ?? null;
        if (idChanged) setRemoteChanges({});
        lastEditingIdRef.current = currentId;
        justSavedRef.current = false;
      }
    } else if (!open) {
      setBase(null);
      baseUpdatedAtRef.current = null;
      lastEditingIdRef.current = null;
      justSavedRef.current = false;
      setRemoteChanges({});
    }
  }, [open, editing, setBase]);

  // 現在の状態と比較して変更があるか
  const isDirty = useMemo(() => {
    if (!open || !editing || !originalJson) return false;
    // コピー時は初期状態で保存可能とする
    if (isCopyReservation) return true;
    return JSON.stringify(editing) !== originalJson;
  }, [editing, originalJson, open, isCopyReservation]);

  // 自分が変更したフィールド（表示用: ヘッダーのサマリー・フィールドの色）
  const myChangedFields = useMemo<string[]>(() => {
    if (!open || !editing || !baseRef.current || isNewReservation || isCopyReservation) return [];
    return diffFields(baseRef.current, editing);
    // originalJson を依存に入れることで base（baseRef）の更新にも追従する
  }, [open, editing, originalJson, isNewReservation, isCopyReservation]);

  // フィールドごとの状態（conflict > dirty > remote）
  const fieldMarks = useMemo<Record<string, FieldMark>>(() => {
    const marks: Record<string, FieldMark> = {};
    Object.values(remoteChanges).forEach((rc) => { marks[rc.field] = 'remote'; });
    myChangedFields.forEach((f) => { marks[f] = 'dirty'; });
    Object.values(remoteChanges).forEach((rc) => {
      if (rc.conflict || myChangedFields.includes(rc.field)) marks[rc.field] = 'conflict';
    });
    return marks;
  }, [remoteChanges, myChangedFields]);

  // ------------------------------------------------------------
  // 他端末の更新を監視（Pusher / ポーリング / 同一画面の保存反映）
  // ------------------------------------------------------------
  const applyRemoteRecord = useCallback((cached: Record<string, any>) => {
    const base = baseRef.current;
    const mine = editingRef.current;
    if (!base || !mine || savingRef.current) return;
    if (!isNewerThanBase(cached, { updated_at: baseUpdatedAtRef.current })) return;

    // base/mine と同じ表現に揃える（editor 形なら toEditorReservation）
    const theirs = isEditorShaped(mine) ? (toEditorReservation(cached) || cached) : cached;
    const diff = diffFields(base, theirs);
    if (diff.length === 0) {
      baseUpdatedAtRef.current = cached.updated_at ?? baseUpdatedAtRef.current;
      return;
    }

    const myDiff = new Set(diffFields(base, mine));
    const patch: Record<string, any> = {};
    const next: Record<string, RemoteChange> = {};
    let hasConflict = false;
    diff.forEach((f) => {
      const value = getField(theirs, f);
      const conflict = myDiff.has(f);
      if (conflict) hasConflict = true;
      else patch[f] = value;
      next[f] = { field: f, value, at: cached.updated_at ?? null, conflict };
    });

    // 触っていないフィールドは最新値に差し替え（editing と base の両方 → dirty にならない）
    if (Object.keys(patch).length > 0) {
      setBase({ ...base, ...patch });
      onChangeRef.current?.(patch as Partial<EditorReservation>);
    }
    // 競合が残っている間は updated_at を進めない（次の通知でも再検出させる）
    if (!hasConflict) baseUpdatedAtRef.current = cached.updated_at ?? baseUpdatedAtRef.current;

    setRemoteChanges((prev) => ({ ...prev, ...next }));
    logEvent({
      action: 'reservation.panel.remote_update',
      target: cached.id,
      result: 'ok',
      detail: { fields: diff, folded: Object.keys(patch), conflicts: diff.filter((f) => myDiff.has(f)) },
    });
  }, [setBase]);

  const editingId = editing ? String(editing.id ?? '') : '';
  useEffect(() => {
    if (!open || !editingId || isNewReservation || isCopyReservation) return undefined;
    const id = editingId;
    if (isTmpId(id)) return undefined;
    const svc: any = reservationSyncService;
    if (!svc?.subscribe || !svc?.cachedReservations) return undefined;

    const listener = (changes: any[]) => {
      // 自分の予約に関係ない Pusher 単発更新は無視（全件同期は id を持たないので必ず確認する）
      if (Array.isArray(changes) && changes.length > 0 && changes.every((c) => c?.id != null && String(c.id) !== id)) return;
      const cached = svc.cachedReservations.get(id);
      if (cached) applyRemoteRecord(cached);
    };
    const unsubscribe = svc.subscribe(listener);
    return () => { unsubscribe?.(); };
    // editing.id が変わった時だけ購読し直す（editing 本体は依存に入れない）
  }, [open, editingId, isNewReservation, isCopyReservation, applyRemoteRecord]);

  // ------------------------------------------------------------
  // 保存（三方向マージ）
  // ------------------------------------------------------------

  /** 競合ダイアログを開いて利用者の選択を待つ。中止なら null */
  const askResolutions = useCallback((conflicts: FieldConflict[], theirs: Record<string, any>) => {
    return new Promise<Record<string, Resolution> | null>((resolve) => {
      conflictResolverRef.current = resolve;
      setConflictDialog({
        open: true,
        conflicts,
        theirsUpdatedAt: theirs.updated_at ?? null,
        theirMemoAdded: memoAddedByTheirs(getField(baseRef.current, 'memo'), getField(theirs, 'memo')),
      });
    });
  }, []);

  const resolveConflictDialog = useCallback((resolutions: Record<string, Resolution> | null) => {
    setConflictDialog(EMPTY_CONFLICT);
    const r = conflictResolverRef.current;
    conflictResolverRef.current = null;
    r?.(resolutions);
  }, []);

  /**
   * サーバー最新を取得して三方向マージし、親の onSave に「保存すべきペイロード」を渡す。
   * 新規・コピーはマージ対象外（そのまま渡す）。
   * @returns 保存を実行したら true（中止・失敗は false）
   */
  const saveMerged = useCallback(async (
    data: EditorReservation,
    options: { keepOpen?: boolean } = {},
  ): Promise<boolean> => {
    const save = onSaveRef.current;
    if (!save) return false;
    const isNew = Boolean(data.__isNew || data.__isCopy);
    const base = baseRef.current;

    if (isNew || !base || isTmpId(data.id)) {
      justSavedRef.current = true;
      await save(data, options);
      return true;
    }

    savingRef.current = true;
    try {
      let fresh: Record<string, any> | null = null;
      try {
        const [f] = await fetchDispatchReservations({ id: data.id });
        fresh = f || null;
      } catch (e) {
        logEvent({ action: 'reservation.panel.fresh_fetch', target: data.id, result: 'fail', err: e });
      }

      let payload: Record<string, any>;
      let mergeSummary: Record<string, any> = {};
      if (!fresh) {
        // 最新を確認できない → 従来どおり editing をそのまま送る（無言にはしない）
        onNoticeRef.current?.('最新の予約データを確認できなかったため、パネルの内容で保存します', 'error');
        payload = { ...data, __completeDetail: true };
        mergeSummary = { mode: 'no_fresh' };
      } else {
        let result = threeWayMerge(base, data, fresh);
        if (result.unresolved.length > 0) {
          const resolutions = await askResolutions(result.unresolved, fresh);
          if (!resolutions) {
            logEvent({
              action: 'reservation.panel.conflict',
              target: data.id,
              result: 'ok',
              detail: { decision: 'cancel', fields: result.unresolved.map((c) => c.field) },
            });
            return false;
          }
          result = threeWayMerge(base, data, fresh, { resolutions });
          logEvent({
            action: 'reservation.panel.conflict',
            target: data.id,
            result: 'ok',
            detail: { decision: resolutions, fields: result.conflicts.map((c) => c.field) },
          });
        }
        payload = { ...result.merged, __completeDetail: true };
        mergeSummary = {
          mode: 'merge',
          mine: result.myChanges,
          theirs: result.theirChanges,
          conflicts: result.conflicts.map((c) => c.field),
          baseUpdatedAt: baseUpdatedAtRef.current,
          freshUpdatedAt: fresh.updated_at ?? null,
        };
        if (result.myChanges.length === 0 && result.theirChanges.length > 0) {
          onNoticeRef.current?.('他の端末の変更を取り込みました（あなたの変更はありません）', 'info');
        }
      }

      justSavedRef.current = true;
      await save(payload as EditorReservation, options);
      logEvent({ action: 'reservation.panel.save', target: data.id, result: 'ok', detail: mergeSummary });

      // 保存できた値を base に反映（keepOpen で開いたままでも dirty / remote 表示が正しくなる）
      const current = editingRef.current;
      if (current && String(current.id) === String(data.id)) {
        setBase({ ...current });
        const cached = reservationSyncService.cachedReservations?.get(String(data.id));
        baseUpdatedAtRef.current = cached?.updated_at ?? fresh?.updated_at ?? baseUpdatedAtRef.current;
      }
      setRemoteChanges({});
      return true;
    } catch (e) {
      logEvent({ action: 'reservation.panel.save', target: data.id, result: 'fail', err: e });
      throw e;
    } finally {
      savingRef.current = false;
    }
  }, [askResolutions, setBase]);

  // 保存ボタン
  const handleSave = useCallback(async () => {
    if (!editing) return;

    // --- 空フィールド検知（データ破損防止） ---
    // 新規・コピー以外で、元々値があったフィールドが空になった場合に警告
    if (!isNewReservation && !isCopyReservation && originalJson) {
      try {
        const original = JSON.parse(originalJson);
        const WATCHED_FIELDS: { key: string; label: string }[] = [
          { key: 'customer_name', label: '予約名' },
          { key: 'car_type', label: '車種' },
          { key: 'vehicle_id', label: '車両' },
          { key: 'pax', label: '乗車人数' },
          { key: 'arrival_flight', label: '到着便' },
          { key: 'departure_flight', label: '出発便' },
          { key: 'contact_media', label: '予約媒体' },
          { key: 'contact_plan', label: 'プラン名' },
          { key: 'base_price', label: '基本料金' },
          { key: 'sum_price', label: '合計金額' },
          { key: 'days', label: '日数' },
          { key: 'nights', label: '泊数' },
          { key: 'comment', label: 'コメント' },
          { key: 'invoice_link', label: 'インボイス' },
          { key: 'customer_email', label: 'メール' },
          { key: 'customer_tel', label: '電話番号' },
        ];

        const isEmpty = (v: any) => v === null || v === undefined || v === '' || v === 0;
        const cleared: string[] = [];

        for (const f of WATCHED_FIELDS) {
          const origVal = original[f.key] ?? original.detail?.[f.key];
          const currVal = (editing as any)[f.key];
          if (!isEmpty(origVal) && isEmpty(currVal)) {
            cleared.push(`${f.label}: ${origVal} → 空`);
          }
        }

        if (cleared.length >= 3) {
          const msg = `⚠️ ${cleared.length}件のフィールドが空になっています:\n\n${cleared.join('\n')}\n\nこのまま保存しますか？`;
          if (!window.confirm(msg)) {
            return; // 保存中止
          }
        }
      } catch {
        // originalJson のパース失敗は無視して保存を続行
      }
    }

    setIsSaving(true);
    try {
      // 自動変更メモを生成して追記
      const autoMemoResult =
        isNewReservation || isCopyReservation
          ? null
          : generateAutoChangeMemo(originalJson, editing, statusOptions);

      let dataToSave: EditorReservation = editing;
      if (autoMemoResult) {
        const newEntry = formatAutoMemoEntry(autoMemoResult);
        const currentMemo = editing.memo || '';
        const updatedMemo = currentMemo ? `${currentMemo}\n${newEntry}` : newEntry;
        // 画面にも反映しつつ、保存データは待たずにここで組む（従来の 300ms 待ちは不要）
        onChange && onChange({ memo: updatedMemo });
        dataToSave = { ...editing, memo: updatedMemo };
      }

      await saveMerged(dataToSave, { keepOpen: false });
    } finally {
      setIsSaving(false);
    }
  }, [editing, isNewReservation, isCopyReservation, originalJson, statusOptions, onChange, saveMerged]);

  // キャンセル処理（originalJsonリセットなし、親に委譲）
  const handleCancel = useCallback(() => {
    // 親コンポーネントの onCancel を呼ぶのは呼び出し側の責務
  }, []);

  // 保存後にoriginalJsonを手動更新する関数（invoice発行等で使用）
  const markSaved = useCallback(() => {
    justSavedRef.current = true;
  }, []);

  /**
   * 一部フィールドだけ別経路で保存済みにする（ステータス即時保存など）。
   * markSaved と違い、他の未保存変更を「保存済み」扱いにしない。
   */
  const markFieldsSaved = useCallback((patch: Record<string, any>) => {
    const base = baseRef.current;
    if (!base) return;
    setBase({ ...base, ...patch });
    setRemoteChanges((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((k) => { delete next[k]; });
      return next;
    });
  }, [setBase]);

  const remoteChangeList = useMemo(() => Object.values(remoteChanges), [remoteChanges]);

  return {
    isSaving,
    isDirty,
    isNewReservation,
    isCopyReservation,
    originalJson,
    handleSave,
    handleCancel,
    markSaved,
    markFieldsSaved,
    saveMerged,
    onSaveRef,
    editingRef,
    justSavedRef,
    // 同時編集の保護
    myChangedFields,
    remoteChanges: remoteChangeList,
    fieldMarks,
    conflictDialog,
    resolveConflictDialog,
  };
}
